import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_CONFIG } from '../../config/apiConfig';
import './index.css';
import TimeRangePicker from './TimeRangePicker';
import LocationSearchInput from '../../components/LocationSearchInput';
import {showToast} from "../../utils/messageUtil";
import { useUser } from "../../contexts/UserContext";
import { handleJSAPIAccess } from '../../utils/auth_access_util';

/**
 * 获取状态时段的样式对象
 */
function getSegmentSlotStyle(left, width, backgroundColor) {
    return {
        position: 'absolute',
        top: 0,
        left: `${left}%`,
        width: width >= 100 ? '100%' : `max(${width}%, 2px)`,
        bottom: 0,
        backgroundColor: backgroundColor,
        pointerEvents: 'none',
        borderRadius: '4px',
        padding: 0,
        margin: 0,
        boxSizing: 'border-box'
    };
}

/**
 * 在 12 小时段上渲染选中时间段的可视指示器
 * - 只选中开始时间：在位置画一个蓝色竖线/标记
 * - 选中完整时间段：画一个蓝色半透明区域
 */
function renderSelectionOverlay(segmentStart, segmentEnd, segmentDuration, selStart, selEnd) {
    const nodes = [];

    // 开始标记颜色（蓝色）
    const START_COLOR = '#1890ff';
    // 结束标记颜色（深红色）
    const END_COLOR = '#cf1322';

    // 如果开始时间在当前段内，标记开始位置
    if (selStart !== null && selStart >= segmentStart && selStart <= segmentEnd) {
        const startOffset = ((selStart - segmentStart) / segmentDuration) * 100;
        nodes.push(
            <div
                key="sel-mark-start"
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${startOffset}%`,
                    width: '4px',
                    backgroundColor: START_COLOR,
                    zIndex: 20,
                    boxShadow: '0 0 8px rgba(24,144,255,0.7)'
                }}
                title={`开始: ${Math.floor(selStart)}:${String(Math.round((selStart % 1) * 60)).padStart(2, '0')}`}
            />
        );
    }

    // 如果有完整的时间段，绘制选中范围
    if (selStart !== null && selEnd !== null) {
        const rangeStart = Math.max(selStart, segmentStart);
        const rangeEnd = Math.min(selEnd, segmentEnd);
        if (rangeStart < rangeEnd) {
            const left = ((rangeStart - segmentStart) / segmentDuration) * 100;
            const width = ((rangeEnd - rangeStart) / segmentDuration) * 100;
            nodes.push(
                <div
                    key="sel-range"
                    style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: `${left}%`,
                        width: width >= 100 ? '100%' : `max(${width}%, 2px)`,
                        backgroundColor: 'rgba(24, 144, 255, 0.45)',
                        zIndex: 15,
                        pointerEvents: 'none',
                        borderLeft: '3px solid ' + START_COLOR,
                        borderRight: '3px solid ' + END_COLOR,
                        boxSizing: 'border-box'
                    }}
                />
            );
        }
    }

    // 如果结束时间在当前段内，标记结束位置
    if (selEnd !== null && selEnd >= segmentStart && selEnd <= segmentEnd) {
        const endOffset = ((selEnd - segmentStart) / segmentDuration) * 100;
        nodes.push(
            <div
                key="sel-mark-end"
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${endOffset}%`,
                    width: '4px',
                    backgroundColor: END_COLOR,
                    zIndex: 20,
                    boxShadow: '0 0 8px rgba(207,19,34,0.7)'
                }}
                title={`结束: ${Math.floor(selEnd)}:${String(Math.round((selEnd % 1) * 60)).padStart(2, '0')}`}
            />
        );
    }

    return nodes;
}

/**
 * 渲染 12 小时段的时间轴（包含顶部时间标签 + 预约时段 + 状态时段）
 * 支持点击选择时间段
 */
function renderTimeAxisSegment(
    vehicle,
    vehicleTimes,
    selectedDate,
    segmentStart,
    segmentEnd,
    status,
    formatTimeFn,
    hoveredReservation,
    popupPosition,
    setPopupPosition,
    setHoveredReservation,
    setShowReservationDetail,
    onSegmentClick,
    selectedStartTime,
    selectedEndTime
) {
    const segmentDuration = segmentEnd - segmentStart;

    // 生成段内的小时刻度
    const hourMarks = [];
    for (let h = segmentStart; h <= segmentEnd; h++) {
        hourMarks.push(h);
    }

    // 计算 reservation 时段（橙色条）
    const dateParts = selectedDate.split('-');
    const selectedDateOnly = new Date(dateParts[0], parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

    const reservationSlotNodes = vehicleTimes.map((timeSlot, index) => {
        const startDateTime = new Date(timeSlot.startTime);
        const endDateTime = new Date(timeSlot.endTime);
        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

        let startHour = 0, endHour = 0;
        if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
            const sParts = timeSlot.startTime.split(' ')[1].split(':');
            const eParts = timeSlot.endTime.split(' ')[1].split(':');
            startHour = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
            endHour = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
        } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
            const sParts = timeSlot.startTime.split(' ')[1].split(':');
            startHour = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
            endHour = 24;
        } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
            const eParts = timeSlot.endTime.split(' ')[1].split(':');
            startHour = 0;
            endHour = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
        } else if (selectedDateOnly > startDateOnly && selectedDateOnly < endDateOnly) {
            startHour = 0;
            endHour = 24;
        } else {
            return null;
        }

        // 裁剪到当前 12 小时段
        const clippedStart = Math.max(startHour, segmentStart);
        const clippedEnd = Math.min(endHour, segmentEnd);
        if (clippedStart >= clippedEnd) return null;

        const left = ((clippedStart - segmentStart) / segmentDuration) * 100;
        const width = ((clippedEnd - clippedStart) / segmentDuration) * 100;

        const reservationId = `${vehicle.vehicleId}-res-${segmentStart}-${segmentEnd}-${clippedStart}-${clippedEnd}-${index}-${vehicleTimes.length}`;

        return (
            <div
                key={reservationId}
                className="time-slot"
                style={{
                    left: `${left}%`,
                    width: width >= 100 ? '100%' : `max(${width}%, 40px)`,
                    backgroundColor: '#faad14',
                    position: 'absolute',
                    top: 0,
                    height: '100%',
                    zIndex: 10,
                    opacity: 0.9,
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0,
                    boxSizing: 'border-box'
                }}
                title={`${formatTimeFn(timeSlot.startTime)} - ${formatTimeFn(timeSlot.endTime)}`}
                onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPopupPosition({
                        top: rect.top - 120,
                        left: rect.left + (rect.width / 2) - 150
                    });
                    setHoveredReservation(timeSlot);
                    setShowReservationDetail(true);
                }}
                onMouseLeave={(e) => {
                    const modal = document.querySelector('.reservation-detail-modal');
                    const isOverModal = modal && modal.contains(e.relatedTarget);
                    const timeSlots = document.querySelectorAll('.time-slot');
                    const isOverOtherTimeSlot = Array.from(timeSlots).some(slot =>
                        slot !== e.currentTarget && slot.contains(e.relatedTarget)
                    );
                    if (!isOverModal && !isOverOtherTimeSlot) {
                        setHoveredReservation(null);
                        setShowReservationDetail(false);
                    }
                }}
                onTouchStart={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPopupPosition({
                        top: rect.top - 120,
                        left: rect.left + (rect.width / 2) - 150
                    });
                    setHoveredReservation(timeSlot);
                    setShowReservationDetail(true);
                }}
                onTouchEnd={() => {
                    setHoveredReservation(null);
                    setShowReservationDetail(false);
                }}
                onTouchCancel={() => {
                    setHoveredReservation(null);
                    setShowReservationDetail(false);
                }}
            ></div>
        );
    }).filter(Boolean);

    // 计算状态时段（绿/黄/灰）
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let currentTime = -1;
    if (selectedDateOnly.getTime() === currentDate.getTime()) {
        currentTime = now.getHours() + now.getMinutes() / 60;
    }

    const reservationSlots = [];
    for (const time of vehicleTimes) {
        const startDateTime = new Date(time.startTime);
        const endDateTime = new Date(time.endTime);
        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

        if (selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly) {
            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                const sParts = time.startTime.split(' ')[1].split(':');
                const eParts = time.endTime.split(' ')[1].split(':');
                const sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                const eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
                reservationSlots.push({ start: sH, end: eH });
            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                const sParts = time.startTime.split(' ')[1].split(':');
                const sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                reservationSlots.push({ start: sH, end: 24 });
            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                const eParts = time.endTime.split(' ')[1].split(':');
                const eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
                reservationSlots.push({ start: 0, end: eH });
            } else {
                reservationSlots.push({ start: 0, end: 24 });
            }
        }
    }

    const mergedSlots = [];
    let currentSlot = null;
    const sortedSlots = [...reservationSlots].sort((a, b) => a.start - b.start);
    for (const slot of sortedSlots) {
        if (!currentSlot) {
            currentSlot = { ...slot };
        } else {
            if (slot.start <= currentSlot.end) {
                currentSlot.end = Math.max(currentSlot.end, slot.end);
            } else {
                mergedSlots.push(currentSlot);
                currentSlot = { ...slot };
            }
        }
    }
    if (currentSlot) mergedSlots.push(currentSlot);

    const finalSlots = [];
    let lastEnd = 0;
    for (const reservedSlot of mergedSlots) {
        // 把 lastEnd 到 min(currentTime, 预约开始) 标为 unavailable
        if (currentTime >= 0 && lastEnd < currentTime) {
            const unavailableEnd = Math.min(reservedSlot.start, currentTime);
            if (unavailableEnd > lastEnd) {
                finalSlots.push({ start: lastEnd, end: unavailableEnd, status: 'unavailable' });
            }
            lastEnd = Math.max(lastEnd, unavailableEnd);
        }
        // lastEnd 到预约开始之间有空 → available
        if (reservedSlot.start > lastEnd) {
            finalSlots.push({ start: lastEnd, end: reservedSlot.start, status: 'available' });
            lastEnd = reservedSlot.start;
        }
        // 预约区间：在当前时间之前的部分 → unavailable，之后的部分 → reserved
        const reservedPastEnd = Math.min(reservedSlot.end, currentTime >= 0 ? currentTime : lastEnd);
        if (lastEnd < reservedPastEnd) {
            finalSlots.push({ start: lastEnd, end: reservedPastEnd, status: 'unavailable' });
            lastEnd = reservedPastEnd;
        }
        if (lastEnd < reservedSlot.end) {
            finalSlots.push({ start: lastEnd, end: reservedSlot.end, status: 'reserved' });
            lastEnd = reservedSlot.end;
        }
    }
    // 循环后，lastEnd 到 24 之间：当前时间之前的 grey，之后的 green
    if (currentTime >= 0 && lastEnd < currentTime) {
        finalSlots.push({ start: lastEnd, end: currentTime, status: 'unavailable' });
        lastEnd = currentTime;
    }
    if (lastEnd < 24) {
        finalSlots.push({ start: lastEnd, end: 24, status: 'available' });
    }
    if (mergedSlots.length === 0) {
        if (currentTime >= 0) {
            finalSlots.push({ start: 0, end: currentTime, status: 'unavailable' });
            finalSlots.push({ start: currentTime, end: 24, status: 'available' });
        } else {
            finalSlots.push({ start: 0, end: 24, status: 'available' });
        }
    }

    const validSlots = finalSlots.filter(slot => slot.start < slot.end);
    const statusSlotNodes = validSlots
        .map((slot, idx) => {
            const clippedStart = Math.max(slot.start, segmentStart);
            const clippedEnd = Math.min(slot.end, segmentEnd);
            if (clippedStart >= clippedEnd) return null;

            const left = ((clippedStart - segmentStart) / segmentDuration) * 100;
            const width = ((clippedEnd - clippedStart) / segmentDuration) * 100;

            let backgroundColor;
            switch (slot.status) {
                case 'available':
                    backgroundColor = 'rgba(82, 196, 26, 0.6)';
                    break;
                case 'reserved':
                    backgroundColor = 'rgba(250, 173, 20, 0.8)';
                    break;
                case 'unavailable':
                    backgroundColor = 'rgba(191, 191, 191, 0.8)';
                    break;
                default:
                    backgroundColor = 'rgba(204, 204, 204, 0.3)';
            }

            return (
                <div
                    key={`status-${vehicle.vehicleId}-${segmentStart}-${segmentEnd}-${clippedStart}-${clippedEnd}-${slot.status}-${idx}`}
                    className={`time-slot-status ${slot.status}`}
                    style={getSegmentSlotStyle(left, width, backgroundColor)}
                ></div>
            );
        })
        .filter(Boolean);

    return (
        <div className="time-axis-segment">
            <div className="time-label">
                {hourMarks.map((h, i) => (
                    <span key={i}>{h}</span>
                ))}
            </div>
            <div
                className={`time-axis ${status === 'unavailable' ? 'unavailable' : ''}`}
                style={{ backgroundColor: 'transparent' }}
                onClick={(e) => onSegmentClick(e, segmentStart, segmentEnd)}
            >
                {reservationSlotNodes}
                {statusSlotNodes}

                {/* 选中时间段可视指示器 */}
                {(selectedStartTime !== null || selectedEndTime !== null) && renderSelectionOverlay(
                    segmentStart, segmentEnd, segmentDuration,
                    selectedStartTime, selectedEndTime
                )}
            </div>
        </div>
    );
}

export default function TimeSelect() {
    const navigate = useNavigate();
    const location = useLocation();
    const { userInfo, isAdmin, isDriver } = useUser();

    // 状态管理
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [vehicleUsageTimes, setVehicleUsageTimes] = useState([]);
    // 可预约时间信息（从车辆选择页面传递过来）
    const [availableTimeInfo, setAvailableTimeInfo] = useState(null);
    // 选择的开始和结束时间（数值型，0-24 范围内）
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    // 预约详情弹窗
    const [showReservationDetail, setShowReservationDetail] = useState(false);
    const [hoveredReservation, setHoveredReservation] = useState(null);
    // 用于跟踪弹窗位置
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
    // 出发地、目的地和用车原因
    const [fromLocation, setFromLocation] = useState('');
    const [toLocation, setToLocation] = useState('');
    const [destinations, setDestinations] = useState([]);
    const [reason, setReason] = useState('');
    // 额外目的地输入框状态
    const [showAddDestination, setShowAddDestination] = useState(false);
    const [additionalDestination, setAdditionalDestination] = useState('');

    // 申请人信息（默认从 userInfo 自动填充）
    const [applicantName, setApplicantName] = useState(userInfo?.name || '');
    const [applicantPhone, setApplicantPhone] = useState((userInfo?.mobile || '').replace(/^\+86/, ''));
    const [applicantDept, setApplicantDept] = useState(userInfo?.department_name || userInfo?.department || '');
    const [applicantId, setApplicantId] = useState(userInfo?.employee_no || '');
    const [applicantOpenId, setApplicantOpenId] = useState(userInfo?.open_id || '');
    // 是否本人申请
    const [isPersonal, setIsPersonal] = useState(true);
    // 用车人信息（当 isPersonal=false 时使用）
    const [vehicleUserName, setVehicleUserName] = useState('');
    const [vehicleUserCode, setVehicleUserCode] = useState('');
    // 同行人
    const [passengers, setPassengers] = useState([]);
    const [passengersCode, setPassengersCode] = useState([]);
    // 提交状态
    const [submitting, setSubmitting] = useState(false);
    // ★ 用 ref 做即时锁，防止 React 状态异步导致的重复提交
    const submittingRef = useRef(false);

    // ===== 多天日期时间选择器状态 =====
    const [showTimeRangePicker, setShowTimeRangePicker] = useState(false);
    // 默认值：开始=当前时间，结束=当前时间+最小用车时间（后台配置）
    const getDefaultDateTimeRange = () => {
        const now = new Date();
        const minMinutes = API_CONFIG.minApplicationMinutes || 60;
        const end = new Date(now.getTime() + minMinutes * 60 * 1000);
        return { start: now, end: end };
    };
    // 未来日期的默认值：开始=所选日期 08:00，结束=09:00
    const getDefaultDateTimeRangeForDate = (dateStr) => {
        return {
            start: new Date(`${dateStr}T08:00:00`),
            end: new Date(`${dateStr}T09:00:00`)
        };
    };
    // 判断是否为未来日期（晚于今天，按本地日期字符串比较，dateStr 格式 YYYY-MM-DD）
    const isFutureDate = (dateStr) => {
        if (!dateStr) return false;
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        return dateStr > todayStr;
    };
    // 当天本地日期字符串（YYYY-MM-DD）—— 用于陈旧日期防护
    const getTodayStr = () => {
        const t = new Date();
        return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
    };
    const defaultDateTime = getDefaultDateTimeRange();
    const [selectedStartDateTime, setSelectedStartDateTime] = useState(defaultDateTime.start);
    const [selectedEndDateTime, setSelectedEndDateTime] = useState(defaultDateTime.end);
    const [isAllDay, setIsAllDay] = useState(false);
    // 用来强制 TimeRangePicker 重新挂载的时间戳
    const [pickerOpenTimestamp, setPickerOpenTimestamp] = useState(0);

    // 手机端（≤768px）检测：用车时间框改用「月日时分」短格式，减少宽度防溢出
    const [isMobileWidth, setIsMobileWidth] = useState(
        typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    );
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const onMqChange = (e) => setIsMobileWidth(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onMqChange);
        else mq.addListener(onMqChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onMqChange);
            else mq.removeListener(onMqChange);
        };
    }, []);

    const openTimeRangePicker = () => {
        setPickerOpenTimestamp(Date.now());
        setShowTimeRangePicker(true);
    };

    // userInfo 加载完成后，更新申请人默认值
    useEffect(() => {
        if (userInfo && !applicantName) {
            setApplicantName(userInfo.name || '');
            setApplicantPhone((userInfo.mobile || '').replace(/^\+86/, ''));
            setApplicantDept(userInfo.department_name || userInfo.department || '');
            setApplicantId(userInfo.user_id || '');
            setApplicantOpenId(userInfo.open_id || '');
        }
    }, [userInfo]);

    // 从URL参数中获取车辆信息和日期
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const vehicleStr = searchParams.get('vehicle');
        const dateStr = searchParams.get('date');
        const availableTimeStr = searchParams.get('availableTime');
        // ★ 陈旧日期防护：WebView 隔夜挂起后 URL 的 date 可能已是昨天（偶发"页面显示昨天日期"根因之一），回落到今天
        const todayStr = getTodayStr();
        const effectiveDate = dateStr && dateStr < todayStr ? todayStr : dateStr;
        // 解析可预约时间信息
        if (availableTimeStr) {
            try {
                const availableTimeData = JSON.parse(decodeURIComponent(availableTimeStr));
                setAvailableTimeInfo(availableTimeData);
            } catch (error) {
                console.error('解析可预约时间信息失败:', error);
                setAvailableTimeInfo(null);
            }
        }
        if (vehicleStr) {
            try {
                const vehicle = JSON.parse(vehicleStr);
                setSelectedVehicle(vehicle);
                // 更新当前车辆的预约时间，使用车牌号和日期
                const targetDate = effectiveDate || selectedDate;
                fetchVehicleAvailableTime(vehicle.plateNumber, targetDate);
            } catch (error) {
                console.error('解析车辆信息失败:', error);
                // 如果解析失败，返回车辆选择页面
                navigate('/vehicle-select');
            }
        } else {
            // 如果没有车辆信息，返回车辆选择页面
            navigate('/vehicle-select');
        }

        if (effectiveDate) {
            setSelectedDate(effectiveDate);
            // 未来日期：默认开始/结束时间为所选日期 8:00 - 9:00
            // （今天及过去日期保持"当前时间 → +最小用车时长"的默认值）
            // 注意：若 sessionStorage 有匹配日期的已存时间（从地图页返回），会在下方覆盖恢复
            if (isFutureDate(effectiveDate)) {
                const futureDefault = getDefaultDateTimeRangeForDate(effectiveDate);
                setSelectedStartDateTime(futureDefault.start);
                setSelectedEndDateTime(futureDefault.end);
            }
        }

        // 检查sessionStorage中是否有存储的时间信息
        const storedTimeInfo = sessionStorage.getItem('selectedTimeInfo');
        const currentDate = effectiveDate || selectedDate;

        if (storedTimeInfo) {
            try {
                const timeData = JSON.parse(storedTimeInfo);
                console.log('从sessionStorage读取时间信息:', timeData);

                // 检查存储的时间是否与当前选择的日期匹配
                // ★ 过期的存储日期（昨天及以前）视为不匹配——防止 WebView 隔夜挂起后恢复出昨天的选择
                const storedDate = timeData.storedDate;
                const isDateMatch = storedDate === currentDate && storedDate >= getTodayStr();

                setSelectedStartTime(timeData.selectedStartTime);
                setSelectedEndTime(timeData.selectedEndTime);

                // ★ 同时恢复模态选择器用的 Date 对象 state（避免从地图返回组件重挂载时被重置为默认时间）
                if (timeData.selectedStartDateTimeISO) {
                    setSelectedStartDateTime(new Date(timeData.selectedStartDateTimeISO));
                }
                if (timeData.selectedEndDateTimeISO) {
                    setSelectedEndDateTime(new Date(timeData.selectedEndDateTimeISO));
                }

                // 恢复存储的reason信息
                if (timeData.reason) {
                    setReason(timeData.reason);
                    console.log('恢复存储的用车原因:', timeData.reason);
                }

                // 如果日期不匹配，清除已选时间
                if (!isDateMatch) {
                    setSelectedStartTime(null);
                    setSelectedEndTime(null);
                    // 未来日期重置为该日期 8:00-9:00，否则保持"当前时间"默认值
                    const resetDefault = isFutureDate(currentDate)
                        ? getDefaultDateTimeRangeForDate(currentDate)
                        : defaultDateTime;
                    setSelectedStartDateTime(resetDefault.start);
                    setSelectedEndDateTime(resetDefault.end);
                    console.log('日期不匹配，清除已选时间');
                }

                console.log('恢复存储的时间信息');
            } catch (error) {
                console.error('解析时间信息失败:', error);
                // 解析失败，清除已选时间（未来日期重置为该日期 8:00-9:00）
                setSelectedStartTime(null);
                setSelectedEndTime(null);
                const resetDefault = isFutureDate(currentDate)
                    ? getDefaultDateTimeRangeForDate(currentDate)
                    : defaultDateTime;
                setSelectedStartDateTime(resetDefault.start);
                setSelectedEndDateTime(resetDefault.end);
                console.log('解析时间信息失败，清除已选时间');
            }
        } else {
            // 首次进入时间选择页面，时间轴无默认选中
            // 时间选择器（selectedStartDateTime）在 useState 中已有默认值
            console.log('首次进入时间选择页面');
        }

        // 清除存储的时间信息（防止重复读取）
        sessionStorage.removeItem('selectedTimeInfo');

    }, [location.search, navigate]);

    // ★ 隔夜挂起守卫：Feishu WebView 可能把页面挂起过夜后恢复（组件不重挂载、URL effect 不重跑），
    //   此时 selectedDate 仍是昨天的。页面重新可见时检测到日期过期 → 重置为今天 + 清空已选时间 + 重拉时间轴
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            const todayStr = getTodayStr();
            if (selectedDate && selectedDate < todayStr) {
                console.log('检测到页面隔夜挂起（selectedDate 已过期），重置为今天:', todayStr);
                setSelectedDate(todayStr);
                setSelectedStartTime(null);
                setSelectedEndTime(null);
                const resetDefault = getDefaultDateTimeRange();
                setSelectedStartDateTime(resetDefault.start);
                setSelectedEndDateTime(resetDefault.end);
                if (selectedVehicle) {
                    fetchVehicleAvailableTime(selectedVehicle.plateNumber, todayStr);
                }
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, selectedVehicle]);

    // 当时间选择变化时，存储到sessionStorage
    useEffect(() => {
        // ★ 条件只依赖 selectedStartDateTime/EndDateTime：
        //   TimeRangePicker 模态确认后 selectedStartTime/EndTime 会被置 null（与时间轴 state 独立），
        //   若依赖它会导致 sessionStorage 不写入 → 从地图返回组件重挂载时时间被重置
        if (selectedStartDateTime && selectedEndDateTime) {
            const timeInfo = {
                selectedStartTime,
                selectedEndTime,
                // 同时持久化模态选择器用的 Date 对象（ISO 字符串），用于从地图返回组件重挂载时恢复
                selectedStartDateTimeISO: selectedStartDateTime.toISOString(),
                selectedEndDateTimeISO: selectedEndDateTime.toISOString(),
                storedDate: selectedDate,
                reason
            };
            sessionStorage.setItem('selectedTimeInfo', JSON.stringify(timeInfo));
            console.log('存储时间信息到sessionStorage:', timeInfo);
        }
    }, [selectedStartTime, selectedEndTime, selectedStartDateTime, selectedEndDateTime, selectedDate, reason]);

    // 检查sessionStorage中是否有选择的地点信息
    const checkLocationInfo = () => {
        // 首先检查selectedLocation（从地图选择页面返回时设置的）
        const selectedLocation = sessionStorage.getItem('selectedLocation');
        console.log('从sessionStorage读取地点信息:', selectedLocation);
        if (selectedLocation) {
            try {
                const locationData = JSON.parse(selectedLocation);
                console.log('解析后的地点信息:', locationData);
                if (locationData.type === 'from') {
                console.log('设置出发地:', locationData.location.name);
                setFromLocation(locationData.location.name);
            } else if (locationData.type === 'to') {
                console.log('设置目的地:', locationData.location.name);
                updateDestinationFromMap(locationData.location.name);
            }
                // 清除sessionStorage中的selectedLocation，避免重复读取
                sessionStorage.removeItem('selectedLocation');
                console.log('清除sessionStorage中的selectedLocation');
            } catch (error) {
                console.error('解析地点信息失败:', error);
            }
        }

        // 直接从sessionStorage中读取出发地和目的地信息
        const storedFromLocation = sessionStorage.getItem('fromLocation');
        const storedToLocation = sessionStorage.getItem('toLocation');

        if (storedFromLocation) {
            console.log('从sessionStorage直接读取出发地:', storedFromLocation);
            setFromLocation(storedFromLocation);
            // 清除sessionStorage中的fromLocation，避免重复读取
            sessionStorage.removeItem('fromLocation');
            console.log('清除sessionStorage中的fromLocation');
        }

        if (storedToLocation) {
            console.log('从sessionStorage直接读取目的地:', storedToLocation);
            updateDestinationFromMap(storedToLocation);
            // 清除sessionStorage中的toLocation，避免重复读取
            sessionStorage.removeItem('toLocation');
            console.log('清除sessionStorage中的toLocation');
        }

        // 打印当前的地点信息状态
        console.log('当前地点信息状态:', {
            fromLocation: fromLocation,
            toLocation: toLocation
        });

        // 打印sessionStorage中的所有地点信息，以便于调试
        console.log('===== sessionStorage中的地点信息 =====');
        console.log('fromLocation:', sessionStorage.getItem('fromLocation'));
        console.log('toLocation:', sessionStorage.getItem('toLocation'));
    };

    // 组件加载时检查地点信息
    useEffect(() => {
        checkLocationInfo();
    }, []);

    // 监听location变化，当从地图选择页面返回时检查地点信息
    useEffect(() => {
        checkLocationInfo();
    }, [location]);

    // 当车辆信息变化时，存储到sessionStorage
    useEffect(() => {
        if (selectedVehicle) {
            const vehicleData = {
                ...selectedVehicle,
                selectedDate: selectedDate
            };
            sessionStorage.setItem('selectedVehicle', JSON.stringify(vehicleData));
        }
    }, [selectedVehicle, selectedDate]);

    // 当selectedVehicle或selectedDate变化时，检查地点信息
    useEffect(() => {
        if (selectedVehicle) {
            console.log('selectedVehicle或selectedDate变化，检查地点信息');
            checkLocationInfo();
        }
    }, [selectedVehicle, selectedDate]);

    // 格式化时间为 "MM-DD HH:mm" 格式
    function formatTime(time) {
        return time.substring(5, 16);
    }

    // 将小时（小数）转换为时间字符串（HH:mm）
    const hourToTimeString = (hourDecimal) => {
        const hours = Math.floor(hourDecimal);
        const minutes = Math.round((hourDecimal - hours) * 60);
        if (minutes === 60) {
            return `${String(hours + 1).padStart(2, '0')}:00`;
        }
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    // 将 Date 对象转换为在时间轴上使用的数值小时（0-24）
    const dateToHourDecimal = (date) => {
        if (!date || isNaN(date.getTime())) return null;
        return date.getHours() + date.getMinutes() / 60;
    };

    // 格式化 Date 为 "MM-DD HH:mm" 显示字符串
    const formatDateTimeDisplay = (date) => {
        if (!date || isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    };
    // 短格式：月日时分（MM-DD HH:mm），手机端用车时间框使用，减少宽度
    const formatShortDateTimeDisplay = (date) => {
        if (!date || isNaN(date.getTime())) return '';
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${month}-${day} ${hours}:${minutes}`;
    };
    // 用车时间框显示：手机端月日时分，桌面端年月日时分
    const formatDateTimeBoxDisplay = (date) =>
        isMobileWidth ? formatShortDateTimeDisplay(date) : formatDateTimeDisplay(date);
    // 时间框日期部分：手机端截掉年份（YYYY-MM-DD → MM-DD）
    const formatDateForBox = (dateStr) =>
        isMobileWidth ? (dateStr || '').slice(5) : (dateStr || '');

    // 从 Date 对象获取日期字符串 "YYYY-MM-DD"
    const dateToDateString = (date) => {
        if (!date || isNaN(date.getTime())) return selectedDate;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // 将 hourDecimal（0-24 数值）+ 日期字符串 转换为 Date 对象（用于时间轴 → 选择器联动）
    const hourDecimalToDate = (dateStr, hourDecimal) => {
        const parts = dateStr.split('-');
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        const h = Math.floor(hourDecimal);
        const m = Math.round((hourDecimal - h) * 60);
        d.setHours(h, m, 0, 0);
        return d;
    };

    // 处理 TimeRangePicker 的确认回调
    const handleTimeRangeConfirm = (startDateTime, endDateTime, allDay) => {
        // 校验用车时长不能低于最小用车时长（租户配置，默认 60 分钟，与派车页行为一致）
        // 不足时：提示 + 开始时间不变，结束时间自动延长到 开始时间+最小时长
        if (startDateTime && endDateTime) {
            const minMinutes = API_CONFIG.minApplicationMinutes || 60;
            const durationMinutes = (endDateTime.getTime() - startDateTime.getTime()) / 60000;
            if (durationMinutes < minMinutes) {
                endDateTime = new Date(startDateTime.getTime() + minMinutes * 60000);
                showToast(`预约时长不能低于${minMinutes}分钟，已自动延长结束时间`, 'warning');
            }
        }
        setSelectedStartDateTime(startDateTime);
        setSelectedEndDateTime(endDateTime);
        setIsAllDay(allDay);
        setShowTimeRangePicker(false);

        // 更新时间轴显示日期
        setSelectedDate(dateToDateString(startDateTime));
        // 清除时间轴上已有的选择区域（时间轴选时与选择器独立）
        setSelectedStartTime(null);
        setSelectedEndTime(null);
    };

    // 获取车辆当天已预约时间
    const fetchVehicleAvailableTime = async (plateNumber, date = selectedDate) => {
        try {
            const params = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                startTime: `${date} 00:00:00`,
                endTime: `${date} 23:59:59`,
                plateNumber: plateNumber
            };

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/getVehicleAvailableTime`, params, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.code === 200) {
                const usageTimes = response.data.data || [];
                console.log('API返回的预约数据:', usageTimes);
                setVehicleUsageTimes(usageTimes);
            } else {
                setVehicleUsageTimes([]);
            }
        } catch (error) {
            console.error('获取车辆已预约时间失败:', error);
            setVehicleUsageTimes([]);
        }
    };

    // 检查指定时间段是否可预约，支持跨天和分钟精度
    const isTimeSlotAvailable = useCallback((startHourDecimal, endHourDecimal, vehicleTimes) => {
        const selectedDateTime = new Date(selectedDate);

        const startHours = Math.floor(startHourDecimal);
        const startMinutes = Math.round((startHourDecimal - startHours) * 60);
        const startDateTime = new Date(selectedDateTime);
        startDateTime.setHours(startHours, startMinutes, 0, 0);

        const endHours = Math.floor(endHourDecimal);
        const endMinutes = Math.round((endHourDecimal - endHours) * 60);
        const endDateTime = new Date(selectedDateTime);
        endDateTime.setHours(endHours, endMinutes, 0, 0);

        const now = new Date();
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const selectedDateOnly = new Date(selectedDateTime.getFullYear(), selectedDateTime.getMonth(), selectedDateTime.getDate());

        // 1. 检查是否与已预约时段重叠
        const sameDayVehicleTimes = vehicleTimes.filter(time => {
            const timeStart = new Date(time.startTime);
            const timeEnd = new Date(time.endTime);
            const startDateOnly = new Date(timeStart.getFullYear(), timeStart.getMonth(), timeStart.getDate());
            const endDateOnly = new Date(timeEnd.getFullYear(), timeEnd.getMonth(), timeEnd.getDate());
            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        if (!checkTimeConflict(startDateTime, endDateTime, sameDayVehicleTimes)) {
            console.log('时间段与已预约时段冲突');
            return false;
        }

        // 2. 检查是否为当前日期且包含已过时间
        if (selectedDateOnly.getTime() === currentDate.getTime()) {
            const currentHourDecimal = now.getHours() + (now.getMinutes() / 60);
            if (startHourDecimal < currentHourDecimal) {
                console.log('时间段包含已过时间:', { startHourDecimal, currentHourDecimal });
                return false;
            }
        }

        return true;
    }, [selectedDate]);

    // 检查两个完整时间段是否冲突
    const checkTimeConflict = (startDateTime, endDateTime, vehicleTimes) => {
        const hasConflict = vehicleTimes.some(time => {
            if (!time.startTime || !time.endTime) {
                return false;
            }

            const timeStart = new Date(time.startTime);
            const timeEnd = new Date(time.endTime);

            if (isNaN(timeStart.getTime()) || isNaN(timeEnd.getTime())) {
                return false;
            }

            return !(endDateTime <= timeStart || startDateTime >= timeEnd);
        });

        return !hasConflict;
    };

    /**
     * 处理时间轴点击选择时间段
     * - 无选择状态：点击设置开始时间
     * - 有开始时间无结束时间：点击设置结束时间（必须在开始时间之后）
     * - 有开始时间和结束时间：重置并重新开始
     */
    /**
     * 把 hour 推进到段内可预约的位置（避免点击落在已预约区间内）
     * 如果 hour 落在 [sH, eH) 这样的预约区间内，把 hour 推到 eH
     * 同时考虑今天已过时间（当前时间之前的部分不可选）
     */
    const pushToAvailableTime = (hour, segmentStart, segmentEnd, vehicleTimes) => {
        const dateParts = selectedDate.split('-');
        const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        const now = new Date();
        const isToday = selectedDateOnly.getFullYear() === now.getFullYear()
            && selectedDateOnly.getMonth() === now.getMonth()
            && selectedDateOnly.getDate() === now.getDate();
        const currentTimeDecimal = isToday ? (now.getHours() + now.getMinutes() / 60) : 0;

        let result = hour;

        // 如果落在已过时间区间 [0, currentTime)，推到 currentTime
        if (result < currentTimeDecimal) {
            result = currentTimeDecimal;
        }

        // 遍历预约区间，如果落在 [sH, eH)，推到 eH
        for (const time of vehicleTimes) {
            const startDateTime = new Date(time.startTime);
            const endDateTime = new Date(time.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());

            let sH, eH;
            if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                const sParts = time.startTime.split(' ')[1].split(':');
                const eParts = time.endTime.split(' ')[1].split(':');
                sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
            } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                const sParts = time.startTime.split(' ')[1].split(':');
                sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                eH = 24;
            } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                const eParts = time.endTime.split(' ')[1].split(':');
                sH = 0;
                eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
            } else {
                sH = 0;
                eH = 24;
            }

            // 把 [sH, eH) 裁剪到 [segmentStart, segmentEnd] 内
            const csH = Math.max(sH, segmentStart);
            const ceH = Math.min(eH, segmentEnd);
            if (csH >= ceH) continue;

            if (result >= csH && result < ceH) {
                result = ceH;
            }
        }

        // 仍超过段边界时裁剪（防止连环推进后超出）
        return Math.max(segmentStart, Math.min(segmentEnd, result));
    };

    const handleSegmentClick = (e, segmentStart, segmentEnd) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const segmentDuration = segmentEnd - segmentStart;
        const clickHour = segmentStart + (clickX / rect.width) * segmentDuration;

        // 四舍五入到最近的 5 分钟，使显示更整洁
        const minutes = (clickHour - Math.floor(clickHour)) * 60;
        const roundedMinutes = Math.round(minutes / 5) * 5;
        const hourRounded = Math.floor(clickHour) + roundedMinutes / 60;

        // 限制在段边界内
        let clampedHour = Math.max(segmentStart, Math.min(segmentEnd, hourRounded));

        // 获取该车辆的预约时间
        const vehicleTimes = vehicleUsageTimes.filter(item => {
            if (item.vehicleId !== selectedVehicle.vehicleId) {
                return false;
            }
            const startDateTime = new Date(item.startTime);
            const endDateTime = new Date(item.endTime);
            const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
            const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
            const dateParts = selectedDate.split('-');
            const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        // 把 clampedHour 推进到段内实际可预约的位置（跳过本段内的预约区间）
        clampedHour = pushToAvailableTime(clampedHour, segmentStart, segmentEnd, vehicleTimes);

        // ★ 校验：推进后如果仍小于当前时间（说明起点在已过期时段内），拒绝该点击
        const datePartsForNow = selectedDate.split('-');
        const selectedDateOnlyForNow = new Date(parseInt(datePartsForNow[0]), parseInt(datePartsForNow[1]) - 1, parseInt(datePartsForNow[2]));
        const nowForNow = new Date();
        const isTodayForNow = selectedDateOnlyForNow.getFullYear() === nowForNow.getFullYear()
            && selectedDateOnlyForNow.getMonth() === nowForNow.getMonth()
            && selectedDateOnlyForNow.getDate() === nowForNow.getDate();
        if (isTodayForNow) {
            const currentHourDecimal = nowForNow.getHours() + nowForNow.getMinutes() / 60;
            // 允许起点 <= 当前时间（秒级误差），但不允许落在不可预约的灰色段
            if (clampedHour < currentHourDecimal - 0.01) {
                showToast('该时间段已过，请选择可预约时段', 'error');
                return;
            }
        }

        if (selectedStartTime === null) {
            // 首次点击 - 设置开始时间
            // 验证该时间点是否可预约（检查是否在已预约时段内）
            // 简单检查：如果点击位置在已预约时段内，提示不可选
            const isInReserved = vehicleTimes.some(time => {
                const startDateTime = new Date(time.startTime);
                const endDateTime = new Date(time.endTime);
                const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
                const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
                const dateParts = selectedDate.split('-');
                const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

                let sH, eH;
                if (selectedDateOnly.getTime() === startDateOnly.getTime() && selectedDateOnly.getTime() === endDateOnly.getTime()) {
                    const sParts = time.startTime.split(' ')[1].split(':');
                    const eParts = time.endTime.split(' ')[1].split(':');
                    sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                    eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
                } else if (selectedDateOnly.getTime() === startDateOnly.getTime()) {
                    const sParts = time.startTime.split(' ')[1].split(':');
                    sH = parseInt(sParts[0], 10) + parseInt(sParts[1], 10) / 60;
                    eH = 24;
                } else if (selectedDateOnly.getTime() === endDateOnly.getTime()) {
                    const eParts = time.endTime.split(' ')[1].split(':');
                    sH = 0;
                    eH = parseInt(eParts[0], 10) + parseInt(eParts[1], 10) / 60;
                } else {
                    sH = 0;
                    eH = 24;
                }
                return clampedHour >= sH && clampedHour < eH;
            });

            if (isInReserved) {
                showToast('该时间段已被预约，请选择其他时间段', 'error');
                return;
            }

            setSelectedStartTime(clampedHour);
            setSelectedEndTime(null);
            // 联动到时间选择器（从时间轴选时 → 更新 Date 对象）
            const startDateObj = hourDecimalToDate(selectedDate, clampedHour);
            setSelectedStartDateTime(startDateObj);
            setSelectedEndDateTime(null);
            setIsAllDay(false);
        } else if (selectedEndTime === null) {
            // 第二次点击 - 设置结束时间
            if (clampedHour <= selectedStartTime) {
                showToast('结束时间必须在开始时间之后', 'error');
                return;
            }

            // 时长校验：短于最小时长 → 提示 + 结束时间自动补足到 开始时间+最小时长（与派车页/选择器行为一致）
            let finalEndHour = clampedHour;
            const minMinutes = API_CONFIG.minApplicationMinutes || 60;
            const durationMinutes = Math.round((clampedHour - selectedStartTime) * 60);
            if (durationMinutes < minMinutes) {
                finalEndHour = selectedStartTime + minMinutes / 60;
                if (finalEndHour > 24) finalEndHour = 24;
                showToast(`预约时长不能低于${minMinutes}分钟，已自动延长结束时间`, 'warning');
            }

            // 验证选中的时间段是否可预约
            if (isTimeSlotAvailable(selectedStartTime, finalEndHour, vehicleTimes)) {
                setSelectedEndTime(finalEndHour);
                // 联动到时间选择器
                const endDateObj = hourDecimalToDate(selectedDate, finalEndHour);
                setSelectedEndDateTime(endDateObj);
            } else {
                showToast('所选时间段不可预约，请选择其他时间段', 'error');
            }
        } else {
            // 已有完整时间段 - 重置并重新开始
            setSelectedStartTime(clampedHour);
            setSelectedEndTime(null);
            // 联动到时间选择器
            const resetStartDateObj = hourDecimalToDate(selectedDate, clampedHour);
            setSelectedStartDateTime(resetStartDateObj);
            setSelectedEndDateTime(null);
            setIsAllDay(false);
        }
    };

    // 出发地/目的地已改为输入框内联地图检索（LocationSearchInput），不再跳转地图选择页面

    // 处理添加额外目的地
    const handleAddDestination = () => {
        if (destinations.length >= 5) {
            showToast('目的地最多只能添加5个', 'error');
            return;
        }

        if (additionalDestination.trim()) {
            const newDestinations = [...destinations, additionalDestination.trim()];
            setDestinations(newDestinations);
            setToLocation(newDestinations.join('，'));
            setAdditionalDestination('');
            setShowAddDestination(false);
        }
    };

    // 删除单个目的地
    const handleRemoveDestination = (index) => {
        const newDestinations = destinations.filter((_, i) => i !== index);
        setDestinations(newDestinations);
        setToLocation(newDestinations.join('，'));
    };

    // 处理目的地输入框手动编辑（保留原有目的地）
    const handleToLocationChange = (e) => {
        const value = e.target.value;
        setToLocation(value);
        if (value) {
            const newDests = value.split('，').filter(d => d.trim());
            setDestinations(newDests);
        } else {
            setDestinations([]);
        }
    };

    // 当从地图选择页面返回时更新目的地
    // 修复 G22：去重避免重复追加
    //   原因：map-select 同时往 sessionStorage 写 toLocation 和 selectedLocation 两个 key，
    //   time-select 的 checkLocationInfo 两个都读 → updateDestinationFromMap 被调两次
    //   第二次 destinations.length > 0 走"追加"分支，导致同一目的地被复制
    const updateDestinationFromMap = (locationName) => {
        if (!locationName) return
        if (destinations.length > 0) {
            // 已选过目的地 → 重复则不追加（避免同值重复出现两次）
            if (destinations.includes(locationName)) {
                return
            }
            // 不同值才追加为途经点
            const newDestinations = [...destinations, locationName]
            setDestinations(newDestinations)
            setToLocation(newDestinations.join('，'))
        } else {
            setDestinations([locationName])
            setToLocation(locationName)
        }
    };

    // 确认选择的时间，返回预约页面
    const confirmTimeSelection = async () => {
        // 支持两种数据源：新的 Date 对象（多天）和旧的数值时间（单天）
        let finalStartDateTime, finalEndDateTime;

        if (selectedStartDateTime && selectedEndDateTime) {
            // 使用 TimeRangePicker 选择的日期时间
            finalStartDateTime = selectedStartDateTime;
            finalEndDateTime = selectedEndDateTime;
        } else if (selectedStartTime !== null && selectedEndTime !== null) {
            // 使用时间轴选择的数值时间（向后兼容）
            finalStartDateTime = new Date(selectedDate);
            const startHours = Math.floor(selectedStartTime);
            const startMinutes = Math.round((selectedStartTime - startHours) * 60);
            finalStartDateTime.setHours(startHours, startMinutes, 0, 0);

            finalEndDateTime = new Date(selectedDate);
            const endHours = Math.floor(selectedEndTime);
            const endMinutes = Math.round((selectedEndTime - endHours) * 60);
            finalEndDateTime.setHours(endHours, endMinutes, 0, 0);
        } else {
            showToast('请选择预约时间段', 'error');
            return;
        }

        // 确保开始时间小于结束时间
        if (finalStartDateTime >= finalEndDateTime) {
            showToast('开始时间必须小于结束时间', 'error');
            return;
        }

        // 校验时长不能低于60分钟
        const durationMinutes = (finalEndDateTime.getTime() - finalStartDateTime.getTime()) / (1000 * 60);
        if (durationMinutes < 60) {
            showToast('预约时长不能低于60分钟', 'error');
            return;
        }

        // 检查出发地、目的地和用车原因是否填写
        if (!fromLocation) {
            showToast('请填写出发地', 'error');
            return;
        }

        if (!toLocation) {
            showToast('请填写目的地', 'error');
            return;
        }

        if (!reason) {
            showToast('请填写用车原因', 'error');
            return;
        }

        console.log('确认选择的时间:', {
            finalStartDateTime,
            finalEndDateTime
        });

        // 检查时间冲突（支持跨天预约）
        const selectedDateStr = finalStartDateTime.toISOString().split('T')[0];
        const dateParts = selectedDateStr.split('-');
        const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));

        const vehicleTimes = vehicleUsageTimes.filter(item => {
            if (item.vehicleId !== selectedVehicle.vehicleId) {
                return false;
            }
            if (!item.startTime || !item.endTime) {
                return false;
            }
            const itemStart = new Date(item.startTime);
            const itemEnd = new Date(item.endTime);
            if (isNaN(itemStart.getTime()) || isNaN(itemEnd.getTime())) {
                return false;
            }
            const startDateOnly = new Date(itemStart.getFullYear(), itemStart.getMonth(), itemStart.getDate());
            const endDateOnly = new Date(itemEnd.getFullYear(), itemEnd.getMonth(), itemEnd.getDate());
            return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
        });

        if (!checkTimeConflict(finalStartDateTime, finalEndDateTime, vehicleTimes)) {
            showToast('所选时间段不可预约，请选择其他时间段', 'error');
            return;
        }

        // 校验申请人信息
        if (!applicantName || !applicantName.trim()) {
            showToast('请填写申请人', 'error');
            return;
        }
        if (!applicantDept || !applicantDept.trim()) {
            showToast('请填写所属部门', 'error');
            return;
        }
        const phoneRegex = /^1[3-9]\d{9}$/;
        if (!applicantPhone || !phoneRegex.test(applicantPhone)) {
            showToast('请输入正确的11位手机号码', 'error');
            return;
        }
        if (!isPersonal) {
            if (!vehicleUserName || !vehicleUserName.trim()) {
                showToast('请填写用车人姓名', 'error');
                return;
            }
        }

        // 转换为 "YYYY-MM-DD HH:mm:ss" 格式
        const formatDateTime = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        };

        const formattedStartTime = formatDateTime(finalStartDateTime);
        const formattedEndTime = formatDateTime(finalEndDateTime);

        // 直接提交申请
        await submitApplication({
            formattedStartTime,
            formattedEndTime,
            fromLocation,
            toLocation,
            destinations,
            reason
        });
    };

    // 提交申请单
    const submitApplication = async ({
        formattedStartTime,
        formattedEndTime,
        fromLocation,
        toLocation,
        destinations,
        reason
    }) => {
        if (submitting) return;
        // ★ ref 即时锁：同一渲染周期内的多次点击也能立刻阻止
        if (submittingRef.current) return;
        try {
            setSubmitting(true);
            submittingRef.current = true;
            const finalVehicleUserName = isPersonal ? applicantName : vehicleUserName;
            const finalVehicleUserCode = isPersonal ? applicantOpenId : vehicleUserCode;

            // 同行人列表：一切以选择的同行人为准，不加申请人本人
            const allPassengerNames = passengers;
            const allPassengerCodes = passengersCode;
            // 后端要的是英文逗号分隔的字符串
            const finalPassengers = allPassengerNames.join(',');
            const finalPassengersCode = allPassengerCodes.join(',');

            const requestData = {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                plateNumber: selectedVehicle.plateNumber,
                applicantId: applicantId,
                applicantOpenId: applicantOpenId,
                applicantName: applicantName,
                applicantPhone: applicantPhone,
                applicantDept: applicantDept,
                applyTime: new Date().toISOString().replace('T', ' ').substring(0, 19),
                reason: reason,
                expectedStartTime: formattedStartTime,
                expectedEndTime: formattedEndTime,
                expectedLocation: fromLocation,
                destination: destinations && destinations.length > 0 ? destinations.join('，') : toLocation,
                driveType: 1,
                applicationType: 0, // 公车申请：0-公车，1-私车
                isPersonal: isPersonal ? 0 : 1,
                vehicleUserName: finalVehicleUserName,
                vehicleUserCode: finalVehicleUserCode,
                passengers: finalPassengers,
                passengersCode: finalPassengersCode
            };

            console.log('提交预约信息:', requestData);

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/submit`, requestData, {
                headers: { 'Content-Type': 'application/json' }
            });

            console.log('API响应:', response.data);

            if (response.data && response.data.code === 200) {
                showToast('预约成功！', 'success');
                sessionStorage.removeItem('selectedTimeInfo');
                sessionStorage.removeItem('fromLocation');
                sessionStorage.removeItem('toLocation');
                // 跳转到预约记录页面（替换当前页，不可返回）
                navigate('/my-reservations', { replace: true });
            } else {
                showToast(`预约失败: ${response.data.message || '未知错误'}`, 'error');
            }
        } catch (error) {
            console.error('提交预约失败:', error);
            showToast('网络错误，请稍后重试', 'error');
        } finally {
            setSubmitting(false);
            submittingRef.current = false;
        }
    };

    // 取消选择，返回车辆选择页面
    const cancelTimeSelection = () => {
        sessionStorage.removeItem('selectedTimeInfo');
        sessionStorage.removeItem('fromLocation');
        sessionStorage.removeItem('toLocation');
        console.log('清除sessionStorage中的时间和地点信息');
        navigate('/vehicle-select');
    };

    // 获取车辆状态
    const getVehicleStatus = (vehicle) => {
        const vehicleTimes = vehicleUsageTimes.filter(item =>
            item.vehicleId === vehicle.vehicleId &&
            item.startTime.split(' ')[0] === selectedDate
        );

        if (vehicleTimes.length === 0) {
            return 'available';
        }

        return 'reserved';
    };

    // ========== 联系人/用车人选择器（从 reserve 迁移） ==========

    // 处理联系人选择结果（同行人）
    const handleContactSelectResult = (res) => {
        console.log('处理联系人选择结果:', res);

        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;
            console.log('选择的用户:', selectedUsers);

            const vehicleUserIds = vehicleUserCode ? vehicleUserCode.split(',').filter(id => id.trim()) : [];
            let filteredByApplicant = 0;
            let filteredByDriver = 0;

            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                if (userOpenId === applicantOpenId) {
                    filteredByApplicant++;
                    return false;
                }
                if (vehicleUserIds.includes(userOpenId)) {
                    filteredByDriver++;
                    return false;
                }
                return true;
            });

            if (filteredUsers.length === 0) {
                if (filteredByApplicant > 0 && filteredByDriver > 0) {
                    showToast('不能选择申请人或已选择的用车人作为同行人，请重新选择', 'error');
                } else if (filteredByApplicant > 0) {
                    showToast('不能选择申请人作为同行人，请重新选择', 'error');
                } else if (filteredByDriver > 0) {
                    showToast('不能选择已选择的用车人作为同行人，请重新选择', 'error');
                } else {
                    showToast('请重新选择同行人', 'error');
                }
                return;
            }

            if (filteredUsers.length < selectedUsers.length) {
                let message = '已排除：';
                const messages = [];
                if (filteredByApplicant > 0) {
                    messages.push('申请人');
                }
                if (filteredByDriver > 0) {
                    messages.push('已选择的用车人');
                }
                message += messages.join('、');
                message += '，不能选择作为同行人';
                showToast(message, 'error');
            }

            const names = filteredUsers.map(user => user.name).filter(name => name);
            // G23 修复：兼容飞书 chooseContact 返回的下划线命名 open_id（驼峰 openId 在实际数据中不存在）
            //   旧代码 user.openId 全是 undefined → openIds 数组被 filter 成空 → passengersCode 传空字符串
            //   后端 StringUtils.hasText("undefined,undefined,undefined") 仍为 true 但查到 null user_id
            //   → 数据库存了 [null,null,null]
            const openIds = filteredUsers.map(user => user.open_id || user.openId || user.user_id).filter(id => id);

            setPassengers(names);
            setPassengersCode(openIds);

            showToast(`已选择 ${names.length} 位联系人`, 'success');
        } else {
            console.warn('未选择联系人');
            showToast('未选择联系人', 'error');
        }
    };

    // 打开飞书联系人选择器（同行人）
    const openLarkContactPicker = async () => {
        console.log('openLarkContactPicker 被调用');
        console.log('window.h5sdk:', window.h5sdk);
        console.log('window.tt:', window.tt);

        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            console.warn('JSAPI鉴权失败，但尝试继续调chooseContact');
        }

        const tt = window.h5sdk.tt || window.tt;
        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持联系人选择', 'error');
            return;
        }

        tt.chooseContact({
            multi: true,
            ignore: false,
            maxNum: 6,
            limitTips: "最多只能选择6个同行人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleContactSelectResult(res);
            },
            fail: (res) => {
                console.log('取消选择同行人:', res);
            }
        });
    };

    // 打开用车人选择器
    const openDriverPicker = async () => {
        console.log('openDriverPicker 被调用');

        const isJSAPIAuthorized = await new Promise((resolve) => {
            if (window.h5sdk && window.h5sdk.config) {
                handleJSAPIAccess((isSuccess) => {
                    resolve(isSuccess);
                });
            } else {
                resolve(false);
            }
        });

        if (!isJSAPIAuthorized) {
            console.warn('JSAPI鉴权失败，但尝试继续调chooseContact');
        }

        const tt = window.h5sdk.tt || window.tt;
        if (!tt || !tt.chooseContact) {
            showToast('当前飞书版本不支持用车人选择', 'error');
            return;
        }

        tt.chooseContact({
            multi: false,
            ignore: false,
            maxNum: 1,
            limitTips: "只能选择1个用车人",
            externalContact: false,
            enableChooseDepartment: false,
            success: (res) => {
                handleDriverSelectResult(res);
            },
            fail: (res) => {
                console.log('取消选择用车人:', res);
            }
        });
    };

    // 处理用车人选择结果
    const handleDriverSelectResult = (res) => {
        console.log('处理用车人选择结果:', res);

        if (res.data && res.data.length > 0) {
            const selectedUsers = res.data;

            const passengerIds = passengersCode.filter(id => id.trim());
            let filteredByApplicant = 0;
            let filteredByPassenger = 0;

            const filteredUsers = selectedUsers.filter(user => {
                const userOpenId = user.open_id || user.openId || user.user_id;
                if (userOpenId === applicantOpenId) {
                    filteredByApplicant++;
                    return false;
                }
                if (passengerIds.includes(userOpenId)) {
                    filteredByPassenger++;
                    return false;
                }
                return true;
            });

            if (filteredUsers.length === 0) {
                if (filteredByApplicant > 0 && filteredByPassenger > 0) {
                    showToast('不能选择申请人或已选择的同行人作为用车人，请重新选择', 'error');
                } else if (filteredByApplicant > 0) {
                    showToast('不能选择申请人作为用车人，请重新选择', 'error');
                } else if (filteredByPassenger > 0) {
                    showToast('不能选择已选择的同行人作为用车人，请重新选择', 'error');
                } else {
                    showToast('请重新选择用车人', 'error');
                }
                return;
            }

            if (filteredUsers.length < selectedUsers.length) {
                let message = '已排除：';
                const messages = [];
                if (filteredByApplicant > 0) {
                    messages.push('申请人');
                }
                if (filteredByPassenger > 0) {
                    messages.push('已选择的同行人');
                }
                message += messages.join('、');
                message += '，不能选择作为用车人';
                showToast(message, 'error');
            }

            const names = filteredUsers.map(user => user.name).filter(name => name);
            const userIds = filteredUsers.map(user => {
                return user.open_id || user.openId || user.user_id;
            }).filter(userId => userId);

            setVehicleUserName(names.join(','));
            setVehicleUserCode(userIds.join(','));

            showToast(`已选择 ${names.length} 个用车人`, 'success');
        } else {
            console.warn('未选择用车人');
            showToast('未选择用车人', 'error');
        }
    };

    // ========== 结束联系人/用车人选择器 ==========

    // 如果没有选择车辆，显示加载状态
    if (!selectedVehicle) {
        return (
            <div className="time-select-page">
                <div className="loading-indicator">
                    <p>加载中...</p>
                </div>
            </div>
        );
    }

    // 获取当前车辆的预约时间（用于时间轴渲染）
    const vehicleTimesForAxis = vehicleUsageTimes.filter(item => {
        if (item.vehicleId !== selectedVehicle.vehicleId) {
            return false;
        }
        const startDateTime = new Date(item.startTime);
        const endDateTime = new Date(item.endTime);
        const startDateOnly = new Date(startDateTime.getFullYear(), startDateTime.getMonth(), startDateTime.getDate());
        const endDateOnly = new Date(endDateTime.getFullYear(), endDateTime.getMonth(), endDateTime.getDate());
        const dateParts = selectedDate.split('-');
        const selectedDateOnly = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
        return selectedDateOnly >= startDateOnly && selectedDateOnly <= endDateOnly;
    });

    return (
        <div className="time-select-page">
            <div className="time-select-content">

                {/* 车辆信息卡片 */}
                <div className="vehicle-info-card">
                    <div className="vehicle-info-title">{selectedVehicle.plateNumber}</div>
                    {selectedVehicle.cityName && <span className="city-tag">{selectedVehicle.cityName}</span>}
                    <div className="vehicle-info-detail">
                        <span>{selectedVehicle.modelName || '未知车型'}</span>
                        <span className="vehicle-seats-tag">{selectedVehicle.seats || selectedVehicle.seatCount || selectedVehicle.capacity || '未知'}座</span>
                        <span className="vehicle-purpose-tag">{selectedVehicle.group || '通用'}</span>
                    </div>
                    <div className="vehicle-info-date">选择日期: {selectedDate}</div>
                </div>

                {/* 合并的时间选择卡片 */}
                <div className="time-selection-card">

                    {/* 状态说明 */}
                    <div className="status-legend-section">
                        <div className="status-item">
                            <span className="status-dot available"></span>
                            <span>可预约</span>
                        </div>
                        <div className="status-item">
                            <span className="status-dot reserved"></span>
                            <span>已预约</span>
                        </div>
                        <div className="status-item">
                            <span className="status-dot unavailable"></span>
                            <span>不可预约</span>
                        </div>
                    </div>

                    {/* 可预约时间摘要 */}
                    {availableTimeInfo && (
                        <div className="time-axis-summary">
                            {availableTimeInfo.text}
                        </div>
                    )}

                    {/* 两段 12 小时时间轴 */}
                    <div className="time-axis-section">
                        <div className="time-axis-container">
                            {/* 第一段 0-12 */}
                            {renderTimeAxisSegment(
                                selectedVehicle,
                                vehicleTimesForAxis,
                                selectedDate,
                                0,
                                12,
                                getVehicleStatus(selectedVehicle),
                                formatTime,
                                hoveredReservation,
                                popupPosition,
                                setPopupPosition,
                                setHoveredReservation,
                                setShowReservationDetail,
                                handleSegmentClick,
                                selectedStartTime,
                                selectedEndTime
                            )}

                            {/* 第二段 12-24 */}
                            {renderTimeAxisSegment(
                                selectedVehicle,
                                vehicleTimesForAxis,
                                selectedDate,
                                12,
                                24,
                                getVehicleStatus(selectedVehicle),
                                formatTime,
                                hoveredReservation,
                                popupPosition,
                                setPopupPosition,
                                setHoveredReservation,
                                setShowReservationDetail,
                                handleSegmentClick,
                                selectedStartTime,
                                selectedEndTime
                            )}
                        </div>

                        {/* 已选择时间信息 - 仅作状态提示，不作为时间选择器入口（入口为下方用车时间框） */}
                        <div className="selected-time-info">
                            {isAllDay ? (
                                <p>
                                    {selectedStartDateTime ? `${formatDateTimeDisplay(selectedStartDateTime)}` : '请选择日期'}
                                    {' — '}
                                    {selectedEndDateTime ? `${formatDateTimeDisplay(selectedEndDateTime)}` : ''}
                                    (全天)
                                </p>
                            ) : (
                                <>
                                    {selectedStartDateTime && selectedEndDateTime ? (
                                        <p>
                                            已选择：{formatDateTimeDisplay(selectedStartDateTime)}
                                            {' — '}
                                            {formatDateTimeDisplay(selectedEndDateTime)}
                                        </p>
                                    ) : selectedStartTime !== null && selectedEndTime !== null ? (
                                        <p>已选择：{hourToTimeString(selectedStartTime)} - {hourToTimeString(selectedEndTime)}</p>
                                    ) : selectedStartTime !== null && selectedEndTime === null ? (
                                        <p>请点击时间轴选择结束时间</p>
                                    ) : (
                                        <p>请点击选择日期时间</p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* 选择方式提示 */}
                    <div className="time-selection-divider">
                        点击时间轴或时间框,如需跨天预约,请点击时间框
                    </div>

                    {/* 合并的时间和地点选择（用车时间 + 出发地/目的地同一模块） */}
                    <div className="combined-selection-section">
                        <div className="time-location-grid">

                            {/* 用车时间：合并为单个时间框（开始 → 结束），格式年月日时分，点击弹出 TimeRangePicker（样式对齐 dispatch/私车公干） */}
                            <div className="form-group">
                                <label>用车时间:<span className="required">*</span></label>
                                <div className="datetime-box-container">
                                    <div className="datetime-box datetime-box-merged" onClick={openTimeRangePicker} title="点击选择用车时间">
                                        <span className="datetime-box-label">开始</span>
                                        <span className="datetime-box-value">
                                            {selectedStartDateTime
                                                ? formatDateTimeBoxDisplay(selectedStartDateTime)
                                                : selectedStartTime !== null
                                                    ? `${formatDateForBox(selectedDate)} ${hourToTimeString(selectedStartTime)}`
                                                    : '请选择'}
                                        </span>
                                        <span className="datetime-box-arrow">→</span>
                                        <span className="datetime-box-label">结束</span>
                                        <span className="datetime-box-value">
                                            {selectedEndDateTime
                                                ? formatDateTimeBoxDisplay(selectedEndDateTime)
                                                : selectedEndTime !== null
                                                    ? `${formatDateForBox(selectedDate)} ${hourToTimeString(selectedEndTime)}`
                                                    : '请选择'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="form-group location-group">
                                <label>出发地:<span className="required">*</span></label>
                                <div className="location-input-container">
                                    <LocationSearchInput
                                        value={fromLocation}
                                        onChange={(val) => setFromLocation(val)}
                                        inputClassName="location-input"
                                        wrapperClassName="lsi-flush"
                                        placeholder="请输入出发地"
                                        required
                                    />
                                </div>
                            </div>


                            <div className="form-group location-group">
                                <label>目的地:<span className="required">*</span></label>
                                <div className="location-content-wrapper">
                                    <div className="location-input-container">
                                        <LocationSearchInput
                                            value={toLocation}
                                            onChange={(val) => handleToLocationChange({ target: { value: val } })}
                                            inputClassName="location-input destination-input"
                                            wrapperClassName="lsi-flush"
                                            placeholder="请输入目的地"
                                            title={toLocation}
                                            required
                                        />
                                    </div>
                                    {/* 已添加的目的地标签 */}
                                    {destinations.length > 0 && (
                                        <div className="destinations-tags">
                                            {destinations.map((dest, index) => (
                                                <span key={index} className="destination-tag">
                                                    {dest}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveDestination(index)}
                                                        className="remove-tag-btn"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {/* 添加目的地按钮 */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (destinations.length >= 5) {
                                                showToast('目的地最多只能添加5个', 'error');
                                                return;
                                            }
                                            setShowAddDestination(!showAddDestination);
                                        }}
                                        className={`add-destination-btn ${destinations.length >= 5 ? 'disabled' : ''}`}
                                        disabled={destinations.length >= 5}
                                    >
                                        ➕ 添加目的地 ({destinations.length}/5)
                                    </button>
                                    {/* 额外目的地输入框 */}
                                    {showAddDestination && (
                                        <div className="additional-destination-container">
                                            <LocationSearchInput
                                                value={additionalDestination}
                                                onChange={(val) => setAdditionalDestination(val)}
                                                inputClassName="location-input additional-destination-input"
                                                placeholder="请输入其他目的地"
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleAddDestination();
                                                    }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddDestination}
                                                className="confirm-add-btn"
                                            >
                                                确认添加
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 申请人信息（本人申请时从userInfo自动填充） */}
                        <div className="applicant-info-section">
                            <div className="form-group">
                                <label>申请人:<span className="required">*</span></label>
                                <div>
                                    <input
                                        type="text"
                                        value={applicantName}
                                        onChange={(e) => setApplicantName(e.target.value)}
                                        required
                                        readOnly
                                        className="form-input"
                                        placeholder="请输入申请人姓名"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>电话号码:<span className="required">*</span></label>
                                <div>
                                    <input
                                        type="tel"
                                        value={applicantPhone}
                                        onChange={(e) => setApplicantPhone(e.target.value)}
                                        required
                                        className="form-input"
                                        placeholder="请输入11位手机号"
                                        maxLength={11}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>所属部门:<span className="required">*</span></label>
                                <div>
                                    <input
                                        type="text"
                                        value={applicantDept}
                                        onChange={(e) => setApplicantDept(e.target.value)}
                                        required
                                        className="form-input"
                                        placeholder="请输入所属部门"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>申请类型:<span className="required">*</span></label>
                                <span className="radio-group" style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
                                    <label className="radio-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <input
                                            type="radio"
                                            name="applyType"
                                            checked={isPersonal === true}
                                            onChange={() => setIsPersonal(true)}
                                            style={{ width: '14px', height: '14px', margin: 0 }}
                                        />
                                        <span>本人申请</span>
                                    </label>
                                    <label className="radio-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        <input
                                            type="radio"
                                            name="applyType"
                                            checked={isPersonal === false}
                                            onChange={() => setIsPersonal(false)}
                                            style={{ width: '14px', height: '14px', margin: 0 }}
                                        />
                                        <span>代人申请</span>
                                    </label>
                                </span>
                            </div>

                            {!isPersonal && (
                                <div className="form-group">
                                    <label>用车人:<span className="required">*</span></label>
                                    <div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                value={vehicleUserName}
                                                readOnly
                                                className="form-input"
                                                placeholder="请选择用车人(最多1个)"
                                                onClick={openDriverPicker}
                                                style={{ flex: 1 }}
                                            />
                                            <button
                                                type="button"
                                                onClick={openDriverPicker}
                                                style={{
                                                    padding: '6px 12px',
                                                    border: '1px solid #d9d9d9',
                                                    borderRadius: '4px',
                                                    background: '#fff',
                                                    cursor: 'pointer',
                                                    fontSize: '18px',
                                                    lineHeight: '1',
                                                    flexShrink: 0
                                                }}
                                                title="选择用车人"
                                            >
                                                👤
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 同行人选择 */}
                            <div className="form-group">
                                <label>同行人:</label>
                                <div>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <input
                                            type="text"
                                            value={passengers.join('、')}
                                            readOnly
                                            className="form-input"
                                            placeholder="选择同行人(最多6个)"
                                            onClick={openLarkContactPicker}
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={openLarkContactPicker}
                                            style={{
                                                padding: '6px 12px',
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '4px',
                                                background: '#fff',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                lineHeight: '1',
                                                flexShrink: 0
                                            }}
                                            title="选择同行人"
                                        >
                                            👥
                                        </button>
                                    </div>
                                    {passengers.length > 0 && (
                                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {passengers.map((name, index) => (
                                                <span key={index} style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    padding: '2px 10px',
                                                    background: '#e6f7ff',
                                                    borderRadius: '4px',
                                                    border: '1px solid #91d5ff',
                                                    fontSize: '13px',
                                                    color: '#333'
                                                }}>
                                                    {name}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const newPassengers = passengers.filter((_, i) => i !== index);
                                                            const newPassengersCode = passengersCode.filter((_, i) => i !== index);
                                                            setPassengers(newPassengers);
                                                            setPassengersCode(newPassengersCode);
                                                        }}
                                                        style={{
                                                            border: 'none',
                                                            background: 'none',
                                                            cursor: 'pointer',
                                                            color: '#999',
                                                            fontSize: '14px',
                                                            padding: '0 2px',
                                                            lineHeight: '1'
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 用车原因 */}
                            <div className="form-group textarea-group">
                                <label>用车原因:<span className="required">*</span></label>
                                <div>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    rows={3}
                                    required
                                    className="reason-textarea"
                                    placeholder="请输入用车原因，接待客户须填写具体客户信息！"
                                ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        className="modal-btn modal-btn-confirm"
                        onClick={confirmTimeSelection}
                        disabled={submitting}
                    >
                        {submitting ? '提交中...' : '立即预约'}
                    </button>
                    <button
                        className="modal-btn modal-btn-cancel"
                        onClick={cancelTimeSelection}
                        disabled={submitting}
                    >
                        取消
                    </button>
                </div>
            </div>
            {/* 预约详情弹窗 */}
            {showReservationDetail && hoveredReservation && (
                <div
                    className="reservation-detail-modal"
                    style={{
                        top: popupPosition.top,
                        left: popupPosition.left
                    }}
                    onMouseEnter={() => {
                        // 进入弹窗时不要关闭
                    }}
                    onMouseLeave={() => {
                        // 鼠标离开弹窗时关闭
                        setHoveredReservation(null);
                        setShowReservationDetail(false);
                    }}
                >
                    <div className="reservation-detail-content">
                        <div className="reservation-detail-body">
                            <div className="reservation-detail-item">
                                <span className="label">预约时间：</span>
                                <span>{formatTime(hoveredReservation.startTime)} - {formatTime(hoveredReservation.endTime)}</span>
                            </div>
                            {hoveredReservation.applicantName && (
                                <div className="reservation-detail-item">
                                    <span className="label">申请人：</span>
                                    <span>{hoveredReservation.applicantName}</span>
                                </div>
                            )}
                            {hoveredReservation.applicantDept && (
                                <div className="reservation-detail-item">
                                    <span className="label">所属部门：</span>
                                    <span>{hoveredReservation.applicantDept}</span>
                                </div>
                            )}
                            {hoveredReservation.applicantPhone && (
                                <div className="reservation-detail-item">
                                    <span className="label">联系电话：</span>
                                    <span>{hoveredReservation.applicantPhone}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* 日期时间范围选择器弹窗 */}
            <TimeRangePicker
                key={`picker-${pickerOpenTimestamp}`}
                open={showTimeRangePicker}
                onClose={() => setShowTimeRangePicker(false)}
                initialStartTime={selectedStartDateTime}
                initialEndTime={selectedEndDateTime}
                isAllDay={isAllDay}
                onConfirm={handleTimeRangeConfirm}
                vehicleTimes={vehicleUsageTimes.filter(item =>
                    item.vehicleId === selectedVehicle?.vehicleId
                )}
            />
        </div>
    );
}
